
import { AIModule } from '../../types';

export const HEALTHCARE_AI_MODULE_1_FOUNDATIONS: AIModule = {
  id: 'healthcare-ai-foundations',
  title: 'Healthcare AI Foundations',
  description: 'Introduction to artificial intelligence applications in healthcare and medical technology.',
  duration: '4 hours',
  difficulty: 'medium',
  content: `
# Healthcare AI Foundations

Artificial Intelligence is transforming healthcare by improving diagnosis accuracy, personalizing treatments, and streamlining medical processes. This module introduces the fundamental concepts and applications of AI in healthcare.

## What is Healthcare AI?

Healthcare AI refers to the application of artificial intelligence technologies to solve medical problems and enhance healthcare delivery. It encompasses machine learning, computer vision, natural language processing, and robotics applied to:

- Medical imaging and diagnostics
- Drug discovery and development
- Electronic health records (EHR) analysis
- Personalized medicine
- Clinical decision support
- Medical robotics
- Telemedicine and remote monitoring

## Key Applications in Healthcare

### 1. Medical Imaging
AI-powered systems that analyze medical images for diagnosis and treatment planning.

**Applications:**
- Radiology image analysis (X-rays, CT, MRI)
- Pathology slide analysis
- Retinal screening
- Skin cancer detection

### 2. Clinical Decision Support
AI systems that assist healthcare providers in making clinical decisions.

**Features:**
- Diagnostic assistance
- Treatment recommendations
- Drug interaction checking
- Risk stratification

### 3. Drug Discovery
AI accelerates the discovery and development of new medications.

**Benefits:**
- Faster target identification
- Optimized compound design
- Improved clinical trial design
- Reduced development costs

## Medical Image Analysis Example

\`\`\`python
import numpy as np
import tensorflow as tf
from tensorflow.keras import layers, models
from sklearn.metrics import classification_report, confusion_matrix
import cv2
import matplotlib.pyplot as plt

class MedicalImageClassifier:
    def __init__(self, image_size=(224, 224), num_classes=2):
        self.image_size = image_size
        self.num_classes = num_classes
        self.model = self._build_model()
        
    def _build_model(self):
        """Build a CNN model for medical image classification"""
        model = models.Sequential([
            # Input layer
            layers.Input(shape=(*self.image_size, 3)),
            
            # First convolutional block
            layers.Conv2D(32, (3, 3), activation='relu'),
            layers.BatchNormalization(),
            layers.MaxPooling2D((2, 2)),
            layers.Dropout(0.25),
            
            # Second convolutional block
            layers.Conv2D(64, (3, 3), activation='relu'),
            layers.BatchNormalization(),
            layers.MaxPooling2D((2, 2)),
            layers.Dropout(0.25),
            
            # Third convolutional block
            layers.Conv2D(128, (3, 3), activation='relu'),
            layers.BatchNormalization(),
            layers.MaxPooling2D((2, 2)),
            layers.Dropout(0.25),
            
            # Fourth convolutional block
            layers.Conv2D(256, (3, 3), activation='relu'),
            layers.BatchNormalization(),
            layers.MaxPooling2D((2, 2)),
            layers.Dropout(0.25),
            
            # Flatten and dense layers
            layers.GlobalAveragePooling2D(),
            layers.Dense(512, activation='relu'),
            layers.BatchNormalization(),
            layers.Dropout(0.5),
            layers.Dense(256, activation='relu'),
            layers.Dropout(0.5),
            layers.Dense(self.num_classes, activation='softmax')
        ])
        
        model.compile(
            optimizer='adam',
            loss='categorical_crossentropy',
            metrics=['accuracy', 'precision', 'recall']
        )
        
        return model
    
    def preprocess_image(self, image_path):
        """Preprocess medical image for model input"""
        # Load image
        image = cv2.imread(image_path)
        image = cv2.cvtColor(image, cv2.COLOR_BGR2RGB)
        
        # Resize image
        image = cv2.resize(image, self.image_size)
        
        # Normalize pixel values
        image = image.astype(np.float32) / 255.0
        
        # Apply medical imaging specific preprocessing
        image = self._enhance_contrast(image)
        image = self._denoise_image(image)
        
        return image
    
    def _enhance_contrast(self, image):
        """Enhance contrast using CLAHE (Contrast Limited Adaptive Histogram Equalization)"""
        # Convert to LAB color space
        lab = cv2.cvtColor(image, cv2.COLOR_RGB2LAB)
        
        # Apply CLAHE to L channel
        clahe = cv2.createCLAHE(clipLimit=2.0, tileGridSize=(8, 8))
        lab[:, :, 0] = clahe.apply((lab[:, :, 0] * 255).astype(np.uint8)) / 255.0
        
        # Convert back to RGB
        enhanced = cv2.cvtColor(lab, cv2.COLOR_LAB2RGB)
        return enhanced
    
    def _denoise_image(self, image):
        """Remove noise from medical image"""
        # Convert to uint8 for denoising
        image_uint8 = (image * 255).astype(np.uint8)
        
        # Apply Non-local Means Denoising
        denoised = cv2.fastNlMeansDenoisingColored(image_uint8, None, 10, 10, 7, 21)
        
        # Convert back to float32
        return denoised.astype(np.float32) / 255.0
    
    def train(self, train_data, validation_data, epochs=50):
        """Train the medical image classifier"""
        # Callbacks for training
        callbacks = [
            tf.keras.callbacks.EarlyStopping(
                monitor='val_loss', patience=10, restore_best_weights=True
            ),
            tf.keras.callbacks.ReduceLROnPlateau(
                monitor='val_loss', factor=0.2, patience=5, min_lr=1e-7
            ),
            tf.keras.callbacks.ModelCheckpoint(
                'best_medical_model.h5', save_best_only=True, monitor='val_accuracy'
            )
        ]
        
        # Train model
        history = self.model.fit(
            train_data,
            epochs=epochs,
            validation_data=validation_data,
            callbacks=callbacks,
            verbose=1
        )
        
        return history
    
    def predict_diagnosis(self, image_path, class_names=['Normal', 'Abnormal']):
        """Predict diagnosis for a medical image"""
        # Preprocess image
        image = self.preprocess_image(image_path)
        image = np.expand_dims(image, axis=0)
        
        # Make prediction
        predictions = self.model.predict(image)
        predicted_class = np.argmax(predictions[0])
        confidence = np.max(predictions[0])
        
        # Generate detailed analysis
        diagnosis = {
            'predicted_class': class_names[predicted_class],
            'confidence': float(confidence),
            'all_probabilities': {
                class_names[i]: float(predictions[0][i]) 
                for i in range(len(class_names))
            },
            'risk_level': self._assess_risk_level(confidence, predicted_class),
            'recommendations': self._generate_recommendations(predicted_class, confidence)
        }
        
        return diagnosis
    
    def _assess_risk_level(self, confidence, predicted_class):
        """Assess risk level based on prediction confidence"""
        if predicted_class == 0:  # Normal
            if confidence > 0.9:
                return 'Low Risk'
            elif confidence > 0.7:
                return 'Low-Medium Risk'
            else:
                return 'Uncertain - Requires Review'
        else:  # Abnormal
            if confidence > 0.9:
                return 'High Risk'
            elif confidence > 0.7:
                return 'Medium-High Risk'
            else:
                return 'Uncertain - Requires Review'
    
    def _generate_recommendations(self, predicted_class, confidence):
        """Generate clinical recommendations"""
        if predicted_class == 0:  # Normal
            if confidence > 0.9:
                return "Continue routine monitoring. No immediate action required."
            else:
                return "Consider follow-up imaging in 6 months or specialist consultation."
        else:  # Abnormal
            if confidence > 0.8:
                return "Urgent specialist consultation recommended. Consider additional imaging."
            else:
                return "Specialist review recommended. Additional diagnostic tests may be needed."

# Clinical Decision Support System
class ClinicalDecisionSupport:
    def __init__(self):
        self.risk_factors = {}
        self.treatment_guidelines = {}
        
    def assess_patient_risk(self, patient_data):
        """Assess patient risk based on multiple factors"""
        risk_score = 0
        risk_factors = []
        
        # Age-based risk
        age = patient_data.get('age', 0)
        if age > 65:
            risk_score += 2
            risk_factors.append('Advanced age')
        elif age > 50:
            risk_score += 1
            risk_factors.append('Moderate age')
        
        # Medical history
        medical_history = patient_data.get('medical_history', [])
        high_risk_conditions = ['diabetes', 'hypertension', 'heart_disease', 'cancer']
        
        for condition in high_risk_conditions:
            if condition in medical_history:
                risk_score += 3
                risk_factors.append(f'History of {condition}')
        
        # Vital signs
        vitals = patient_data.get('vitals', {})
        if vitals.get('blood_pressure_systolic', 0) > 140:
            risk_score += 2
            risk_factors.append('Elevated blood pressure')
        
        if vitals.get('heart_rate', 0) > 100:
            risk_score += 1
            risk_factors.append('Elevated heart rate')
        
        # Laboratory results
        lab_results = patient_data.get('lab_results', {})
        if lab_results.get('glucose', 0) > 126:
            risk_score += 2
            risk_factors.append('Elevated glucose')
        
        # Determine risk level
        if risk_score >= 8:
            risk_level = 'Very High'
        elif risk_score >= 6:
            risk_level = 'High'
        elif risk_score >= 4:
            risk_level = 'Moderate'
        elif risk_score >= 2:
            risk_level = 'Low-Moderate'
        else:
            risk_level = 'Low'
        
        return {
            'risk_score': risk_score,
            'risk_level': risk_level,
            'risk_factors': risk_factors,
            'recommendations': self._generate_clinical_recommendations(risk_level, risk_factors)
        }
    
    def _generate_clinical_recommendations(self, risk_level, risk_factors):
        """Generate clinical recommendations based on risk assessment"""
        recommendations = []
        
        if risk_level in ['Very High', 'High']:
            recommendations.extend([
                'Immediate medical attention required',
                'Consider hospitalization or intensive monitoring',
                'Implement aggressive treatment protocols'
            ])
        elif risk_level == 'Moderate':
            recommendations.extend([
                'Close monitoring required',
                'Follow-up within 1-2 weeks',
                'Consider specialist consultation'
            ])
        else:
            recommendations.extend([
                'Routine monitoring appropriate',
                'Follow standard care protocols',
                'Regular check-ups as scheduled'
            ])
        
        # Specific recommendations based on risk factors
        if 'diabetes' in str(risk_factors).lower():
            recommendations.append('Monitor blood glucose levels closely')
        
        if 'hypertension' in str(risk_factors).lower():
            recommendations.append('Monitor blood pressure regularly')
        
        return recommendations

# Example usage:
# classifier = MedicalImageClassifier()
# diagnosis = classifier.predict_diagnosis('chest_xray.jpg', ['Normal', 'Pneumonia'])
# 
# cds = ClinicalDecisionSupport()
# risk_assessment = cds.assess_patient_risk(patient_data)
\`\`\`

## Ethical Considerations in Healthcare AI

### Patient Privacy and Data Security
- HIPAA compliance
- Data anonymization
- Secure data transmission
- Consent management

### Bias and Fairness
- Training data diversity
- Algorithm fairness testing
- Disparate impact assessment
- Continuous monitoring

### Transparency and Explainability
- Model interpretability
- Decision audit trails
- Patient understanding
- Clinical validation

Healthcare AI has the potential to revolutionize medical care by improving diagnostic accuracy, personalizing treatments, and making healthcare more accessible and efficient.
  `,
  quiz: {
    id: 'healthcare-ai-foundations-quiz',
    title: 'Healthcare AI Foundations Quiz',
    passThreshold: 80,
    questions: [
      {
        id: 'q1',
        question: 'What is one of the main benefits of AI in medical imaging?',
        type: 'multiple-choice',
        options: [
          'It replaces all radiologists',
          'It improves diagnostic accuracy and consistency',
          'It reduces the need for medical training',
          'It eliminates the need for human oversight'
        ],
        correctAnswer: 1,
        explanation: 'AI in medical imaging enhances diagnostic accuracy and provides consistent analysis, but it works alongside medical professionals rather than replacing them.',
        difficulty: 'medium',
        points: 15
      }
    ]
  },
  resources: [
    {
      id: 'healthcare-ai-video',
      title: 'Healthcare AI Overview',
      type: 'video',
      url: 'https://example.com/healthcare-ai',
      description: 'Introduction to AI applications in healthcare'
    }
  ]
};
